import { useEffect, useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton } from "ui-components/IconButton";
import { Success, Error_, MessageDialog } from "ui-components/Alert";
import BasicTabs from "ui-components/BasicTabs";
import { CancelButton } from "ui-components/IconButton";

import { useCustomerValue } from "providers/CustomerContext";

import LoadManifestLog from "./components/LoadManifestLog";
import { getManifestList } from "services/data.outbound.service";
import { getManifestStatusCount } from "services/data.outbound.service";
import { deleteLoadManifest } from "services/data.outbound.service";
import { TableCard } from "ui-components/Layout";
import { getManifestReport } from "services/data.report.service";
import { PrintButton } from "ui-components/IconButton";
import { useTeamValue } from "providers/TeamContext";
import { LoadingDialog } from "ui-components/Alert";

const statusObj = { Open: "L", Cancelled: "C" };

function ManifestList() {
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const enabled = Boolean(customerCode);
  const navigate = useNavigate();
  const location = useLocation();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("Open");
  const [tabs, setTabs] = useState([
    { label: "Open", count: 0 },
    { label: "Cancelled", count: 0 },
  ]);
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const addPermission =
    allowAll ||
    (Boolean(permissions["Load Manifest"]) &&
      permissions["Load Manifest"].findIndex((b) => b === "new") !== -1);

  useEffect(() => {
    if (location.state) {
      setAlert(<Success content={location.state} onChange={setAlert} />);
    }
  }, [location.state]);

  useQuery({
    queryKey: ["manifest-list", { customerCode, status }],
    queryFn: () => getManifestList({ customerCode, status: statusObj[status] }),
    enabled,
    onSuccess: (data) => setData(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["manifest-list", "status", { customerCode }],
    queryFn: () => getManifestStatusCount({ customerCode }),
    enabled,
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deleteLoadManifest, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["manifest-list"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });

  const downloadReportMutation = useMutation(getManifestReport, {
    onSuccess: (response) => {
      const fileType = response.headers["content-type"];
      const blob = new Blob([response.data], {
        type: fileType,
      });
      const url = window.URL.createObjectURL(blob);
      if (fileType.includes("pdf")) {
        const w = window.open(url);
        w.print();
        //a.href = null;
        window.URL.revokeObjectURL(url);
      }
    },
    onError: () => setAlert(<Error_ content="The report file not found" onChange={setAlert} />),
  });

  const onEdit = (req) => {
    navigate("/outbound/load-manifest/edit", {
      state: req,
    });
  };

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Cancel Manifest Number ${req.manifestNumber}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const deleteStatus = delMutation.status === "loading";
  const downloadStatus = downloadReportMutation.status === "loading"

  const [logRequest, setLogRequest] = useState(null);
  const [open, setOpen] = useState(false);
  const logProp = { open, setOpen, logRequest, setLogRequest };

  const columns = [
    {
      header: "Manifest Number",
      accessorKey: "manifestNumber",
      cell: ({ row }) => {
        const { manifestNumber,manifestId } = row.original;
        return (
          <span
            style={{ color: "#1A73E8", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              setOpen(true);
              setLogRequest({manifestId, manifestNumber, customerCode,status: statusObj[status] });
            }}
          >
            {manifestNumber}
          </span>
        );
      },
    },
    { header: "Loading Datetime", accessorKey: "loadingDatetime" },
    { header: "Carrier", accessorKey: "carrier" },
    { header: "Truck No", accessorKey: "truckNo" },
    { header: "Transport Arrangement", accessorKey: "transportArrangement", width: "200px" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { manifestNumber, canCancel } = row.original;
        return status === "Open" ? (
          <>
            {/* <EditButton
              title="Edit Load Manifest"
              onClick={() => {
                onEdit({ manifestNumber });
              }}
            /> */}
            <PrintButton
              title="Print Manifest"
              onClick={() => {
                downloadReportMutation.mutate({ manifest_numbers: [manifestNumber] });
              }}
            />
            {canCancel && (
              <CancelButton
                title="Cancel Load Manifest"
                onClick={() => {
                  onDelete({ customerCode, manifestNumber });
                }}
              />
            )}
          </>
        ) : (
          <></>
        );
      },
    },
  ];
  return (
    <ListLayout>
      <LoadManifestLog {...logProp} />
      {(downloadStatus || deleteStatus) && <LoadingDialog />}

      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" margin="10px">
          <MDButton
            variant="gradient"
            color="info"
            disabled={!addPermission}
            onClick={() => navigate("/outbound/load-manifest/add")}
          >
            <Icon>add</Icon>&nbsp;manifest
          </MDButton>
        </MDBox>

        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default ManifestList;
